import {
  persistReducer,
  createAsyncThunk,
  createSelector,
  createSlice,
  injectSlice,
} from '@bonanza-social/toolkit/state';
import { GlobalAlertState } from './globalAlerts.types';
import { AsyncStorage } from '@bonanza-social/toolkit/storage';
import GlobalAlertService from '../services/GlobalAlertService/GlobalAlertService';

const initialState: GlobalAlertState = {
  data: undefined,
  acknowledgedIds: [],
  dataState: 'idle',
};

export const fetchGlobalAlert = createAsyncThunk(
  'globalAlert/fetch',
  async () => {
    return await GlobalAlertService.getGlobalAlert();
  },
);

const globalAlert = createSlice({
  name: 'globalAlert',
  initialState,
  reducers: {
    acknowledgeGlobalAlert(state) {
      if (state.data?.id) {
        state.acknowledgedIds = [...state.acknowledgedIds, state.data.id];
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGlobalAlert.pending, (state) => {
      state.dataState = 'pending';
    });
    builder.addCase(fetchGlobalAlert.fulfilled, (state, action) => {
      state.data = action.payload;
      state.dataState = 'fulfilled';
    });
    builder.addCase(fetchGlobalAlert.rejected, (state) => {
      state.dataState = 'rejected';
    });
  },
});

const persistConfig = {
  key: 'globalAlert',
  storage: AsyncStorage,
  whitelist: ['acknowledgedIds'],
};

export const persistedReducer = persistReducer(
  persistConfig,
  globalAlert.reducer,
);

export const withGlobalAlert = injectSlice({
  reducerPath: 'globalAlert',
  reducer: persistedReducer,
});

export const { acknowledgeGlobalAlert } = globalAlert.actions;

export const selectGlobalAlert = withGlobalAlert.selector(
  (state) => state.globalAlert.data,
);

export const selectGlobalAlertAcknowledgedIds = withGlobalAlert.selector(
  (state) => state.globalAlert.acknowledgedIds,
);

export const selectShowGlobalAlert = createSelector(
  [selectGlobalAlert, selectGlobalAlertAcknowledgedIds],
  (alert, acknowledgedIds) => {
    const acknowledgedIdsSet = new Set(acknowledgedIds);
    return alert && !acknowledgedIdsSet.has(alert.id);
  },
);

export const selectGlobalAlertDataState = withGlobalAlert.selector(
  (state) => state.globalAlert.dataState,
);

declare global {
  interface LazyLoadedSlices {
    globalAlert: ReturnType<typeof persistedReducer>;
  }
}
